import loggingService from "@app/util/logging";

import voiceSettings from "./voiceSettings";

// Starting points, not derived values — the right answer differs between a quiet office and a
// truck cab, which is what the override mechanism below exists for. Only values that need
// calibrating against real field audio belong here; the meter's looks are settled and sit below as
// plain consts.
export const VOICE_TUNABLES = {
  DEAD_MIC_RMS: 0.001, //        below this is no signal at all, not a quiet room
  DONE_HINT_SILENCE_MS: 5_000, // silence after speech before nudging Done
  MAX_CLIP_MS: 55_000, //        under the server's 60s cap
  METER_FLOOR_RMS: 0.004, //     maps to display level 0; a truck cab is not a quiet office
  MIN_CLIP_MS: 400, //           floor on clip length; blips never become clips of their own
  NO_SIGNAL_MS: 5_000, //        flat-signal window before warning about the microphone
  SILENCE_MS: 800, //            quiet time before cutting a clip
  SPEECH_ENTER_RMS: 0.006, //    hysteresis: harder to enter speech...
  SPEECH_STAY_RMS: 0.003, //     ...than to stay in it
} as const;

export type VoiceTunableName = keyof typeof VOICE_TUNABLES;

const PARAM_PREFIX = "mmc-voice-input-";
const RESET_PARAM = `${PARAM_PREFIX}reset`;

// Kebab parameter to storage name, mechanically — a lookup table would be a second list of names
// to drift from the first. voiceSettings turns the name into the actual key.
const paramName = (name: string) => `${PARAM_PREFIX}${name.toLowerCase().replace(/_/g, "-")}`;

const TUNABLE_NAMES = Object.keys(VOICE_TUNABLES) as VoiceTunableName[];

export const applyVoiceTunableOverrides = (search: string): void => {
  const params = new URLSearchParams(search);

  // Without a way to clear these, someone tunes once, forgets, and files a phantom bug weeks
  // later that nobody can reproduce.
  if (params.has(RESET_PARAM)) {
    TUNABLE_NAMES.forEach((name) => voiceSettings.clearOverride(name));
    return;
  }

  TUNABLE_NAMES.forEach((name) => {
    const raw = params.get(paramName(name));
    if (raw === null) {
      return;
    }
    if (raw === "") {
      voiceSettings.clearOverride(name);
      return;
    }
    const value = Number(raw);
    // A typo would otherwise become NaN, every RMS comparison against it would be false, and
    // clipping would silently stop happening at all. METER_FLOOR_RMS additionally rejects zero:
    // log10(0) is -Infinity, which turns every bar height into NaN and blanks the meter with no
    // throw and no other log to point at why.
    const isOutOfRange =
      !Number.isFinite(value) || value < 0 || (name === "METER_FLOOR_RMS" && value === 0);
    if (isOutOfRange) {
      loggingService.warning("Ignoring an out-of-range voice tunable override", { name });
      return;
    }
    voiceSettings.setOverride(name, String(value));
  });
};

// Read per call rather than cached at module load: the whole point is that changing the URL and
// pressing record again is enough, with no reload.
export const getVoiceTunable = (name: VoiceTunableName): number => {
  const stored = voiceSettings.getOverride(name);
  if (stored === null) {
    return VOICE_TUNABLES[name];
  }
  const value = Number(stored);
  return Number.isFinite(value) && value >= 0 ? value : VOICE_TUNABLES[name];
};

export type VoiceTunables = Readonly<Record<VoiceTunableName, number>>;

// Resolved once per recording session, not per call. getVoiceTunable() is a synchronous
// localStorage read by design, but the analyser ticks every 20ms and each tick reaches five to
// eight of them through isSpeech, reduceHint and the clip guards — several hundred reads a second,
// for the whole recording, on the low-end device least able to afford them. Snapshotting keeps the
// override workflow intact: the values are re-read when the rep presses record again, which is
// exactly what the per-call read existed for.
//
// Spelled out rather than built from TUNABLE_NAMES: written this way the Record is checked for
// totality, so a new tunable fails to compile until it is added here too.
export const readVoiceTunables = (): VoiceTunables => ({
  DEAD_MIC_RMS: getVoiceTunable("DEAD_MIC_RMS"),
  DONE_HINT_SILENCE_MS: getVoiceTunable("DONE_HINT_SILENCE_MS"),
  MAX_CLIP_MS: getVoiceTunable("MAX_CLIP_MS"),
  METER_FLOOR_RMS: getVoiceTunable("METER_FLOOR_RMS"),
  MIN_CLIP_MS: getVoiceTunable("MIN_CLIP_MS"),
  NO_SIGNAL_MS: getVoiceTunable("NO_SIGNAL_MS"),
  SILENCE_MS: getVoiceTunable("SILENCE_MS"),
  SPEECH_ENTER_RMS: getVoiceTunable("SPEECH_ENTER_RMS"),
  SPEECH_STAY_RMS: getVoiceTunable("SPEECH_STAY_RMS"),
});

// One scalar level per frame — the RMS the analyser already computes. No frequency analysis:
// getByteFrequencyData and getFloatFrequencyData are the wrong branch. A spectrogram is too
// technical for a field rep, and a frequency-bucket equalizer moves whether or not useful audio is
// being captured, so it cannot answer the one question being asked — "is it hearing me".
export const METER_BARS_PER_SEC = 15;
export const METER_BAR_WIDTH_PX = 5;
export const METER_BAR_GAP_PX = 3;
export const METER_MAX_BARS = 240;
export const METER_CEIL_RMS = 0.25;
// Cosmetic tail so a loud syllable ramps down instead of cliffing. 0 gives a literal reading.
export const METER_DECAY = 0.5;
export const METER_MAX_CATCHUP_BUCKETS = 4;

// The meter is centred: bars mirror around the panel's vertical centre, behind the record icon and
// the timecode. A bottom-anchored variant was built alongside it and compared on real recordings;
// this one was chosen and the other deleted, so there is one geometry rather than a branch.
//
// Resting height fraction, so silence still shows something rather than a blank panel.
export const METER_PEAK_FLOOR = 0.08;

// Settled by eye against real recordings, so no longer runtime-overridable. The blur is what turns
// the bars into a soft field rather than a chart; the opacity keeps it behind the record icon and
// the timecode instead of competing with them.
export const METER_BLUR_PX = 12;
export const METER_OPACITY = 0.5;
